/**
 * Slow Maui Agent Portal - Main Entry Point
 */
import {
  initializeFirebase,
  signInWithGoogle,
  signInWithApple,
  signOut,
  onAuthStateChange,
  getIdToken,
  type AuthUser,
} from '@slowmaui/auth';
import { AgentClient, ApiError, DataClient, type AgentAuthStatus } from '@slowmaui/api-client';
import type { NeighborhoodSummary } from '@slowmaui/shared-types';
import { formatListingAddress } from '@slowmaui/shared-types';
import { router } from './router';
import { setCanEditDefaults, canUseAdminMode, isAdminMode, setAdminMode } from './adminMode';
import { setPendingMapIntent } from './state/mapIntent';
import { CartDrawer } from './components/CartDrawer';
import { setCartDrawer } from './state/cartDrawer';
import { ListDrawer } from './components/ListDrawer';
import { setListDrawer } from './state/listDrawer';
import { getSelectedContact, onSelectedContactChange, clearSelectedContact } from './state/selectedContact';

const dataApi = new DataClient({
  baseUrl: import.meta.env.VITE_DATA_API_URL || 'https://api.slowmaui.com',
  debug: import.meta.env.DEV,
});

// App-shell-level CartDrawer state (see state/cartDrawer.ts) - instantiated
// once on first approved login, not per-page the way the old AgentDrawer
// was. shellNeighborhoods is this module's own copy of the same cheap
// getNeighborhoods() call AgentMap.ts already makes, needed for the
// drawer's synchronous resolveNeighborhoodLabel - duplicating one existing
// call rather than adding new data-api surface or a cross-module
// dependency on AgentMap's own in-memory array.
let cartDrawer: CartDrawer | null = null;
let listDrawer: ListDrawer | null = null;
let selectedContactListenerWired = false;
let shellNeighborhoods: NeighborhoodSummary[] = [];
let currentPage = '';

// Map is a full-bleed canvas - floating drawers over it is the norm (same
// as the map's other drawers). Messaging/Contacts/Recent Changes are
// ordinary scrolling page content, where a drawer covering the left edge
// would hide real controls - those get #main-content shrunk instead, so
// the open panel sits beside the content rather than on top of it. Cart
// and List are never both available on the same page (Cart: map/
// messaging/market-activity; List: contacts only - see the route-change
// handler below), so only one of the two can ever be open at a time.
const SHRINK_MAIN_CONTENT_PAGES = new Set(['messaging', 'contacts', 'market-activity']);

function updateMainContentShrink(): void {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;
  const openDrawer = cartDrawer?.isOpen() ? cartDrawer : (listDrawer?.isOpen() ? listDrawer : null);
  const shouldShrink = SHRINK_MAIN_CONTENT_PAGES.has(currentPage) && !!openDrawer;
  // Reads the drawer's current width live (not a fixed constant) - it's
  // now user-resizable (per Todd 2026-10-25), so this has to stay in sync
  // via onResize below rather than a static import.
  mainContent.style.marginLeft = shouldShrink ? `${openDrawer?.getWidth() ?? 0}px` : '';
}

// Firebase configuration from environment
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// Initialize Firebase Auth
initializeFirebase(firebaseConfig);

// Type alias
type User = AuthUser;

// DOM Elements
const authGate = document.getElementById('auth-gate') as HTMLDivElement;
const agentLayout = document.getElementById('agent-layout') as HTMLDivElement;
const signInGoogleBtn = document.getElementById('sign-in-google-btn') as HTMLButtonElement;
const signInAppleBtn = document.getElementById('sign-in-apple-btn') as HTMLButtonElement;
const signOutBtn = document.getElementById('sign-out-btn') as HTMLButtonElement;
const userAvatar = document.getElementById('user-avatar') as HTMLImageElement;
const userName = document.getElementById('user-name') as HTMLSpanElement;
const userMenuBtn = document.getElementById('user-menu-btn') as HTMLButtonElement;
const userDropdown = document.getElementById('user-dropdown') as HTMLDivElement;
const pageContent = document.getElementById('page-content') as HTMLDivElement;
const navItems = document.querySelectorAll('.top-tab');
const hamburgerBtn = document.getElementById('hamburger-btn') as HTMLButtonElement;
const topTabsNav = document.getElementById('top-tabs') as HTMLElement;
const agentSubdomain = document.getElementById('agent-subdomain') as HTMLDivElement;
const selectedContactBadge = document.getElementById('selected-contact-badge') as HTMLDivElement;
const selectedContactName = document.getElementById('selected-contact-name') as HTMLSpanElement;
const selectedContactClear = document.getElementById('selected-contact-clear') as HTMLButtonElement;
const adminModeBadge = document.getElementById('admin-mode-badge') as HTMLSpanElement;
const adminModeRow = document.getElementById('admin-mode-row') as HTMLDivElement;
const adminModeToggle = document.getElementById('admin-mode-toggle') as HTMLInputElement;
const seoNavItem = document.getElementById('seo-nav-item') as HTMLAnchorElement;
const suggestionsNavItem = document.getElementById('suggestions-nav-item') as HTMLAnchorElement;

// Global state
let currentUser: User | null = null;
let agentClient: AgentClient | null = null;
let agentStatus: AgentAuthStatus | null = null;

// Legacy interface for backward compatibility with router
interface AgentProfile {
  id: string;
  subdomain: string;
  name: string;
  email: string;
  phone?: string;
  photo?: string;
  bio?: string;
  branding?: {
    primaryColor?: string;
    logo?: string;
  };
  // Progressive-access fields (per Todd 2026-10-24) - threaded through so
  // pages can show a pending/billing banner or disable publish-adjacent
  // actions without an extra API call. Only 'pending'/'approved' ever reach
  // here - proceedWithAgentStatus already returns early for rejected/
  // suspended before toAgentProfile is ever called.
  status: 'pending' | 'approved';
  billingStatus: string;
  trialEndsAt: string | null;
}

// Convert AgentAuthStatus to legacy AgentProfile format
function toAgentProfile(status: AgentAuthStatus): AgentProfile | null {
  return {
    id: status.id,
    subdomain: status.subdomain || '',
    name: status.name || '',
    email: status.email,
    phone: status.phone || undefined,
    photo: status.photo_url || undefined,
    bio: status.bio || undefined,
    status: status.status as 'pending' | 'approved',
    billingStatus: status.billing_status || 'none',
    trialEndsAt: status.trial_ends_at,
  };
}

/**
 * Get agent subdomain from URL
 */
function getAgentSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Check for subdomain pattern: agent.slowmaui.com
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[1] === 'slowmaui') {
    return parts[0];
  }

  // For local development, check URL param
  const params = new URLSearchParams(window.location.search);
  return params.get('agent');
}

/**
 * Show auth gate, hide agent layout
 */
function showAuthGate(message?: string): void {
  authGate.classList.remove('hidden');
  agentLayout.classList.add('hidden');

  // Clear any previous status screens
  const existingStatus = document.getElementById('status-screen');
  if (existingStatus) {
    existingStatus.remove();
  }

  if (message) {
    const existingError = authGate.querySelector('.auth-error');
    if (existingError) {
      existingError.textContent = message;
    } else {
      const errorDiv = document.createElement('p');
      errorDiv.className = 'auth-error';
      errorDiv.textContent = message;
      authGate.querySelector('.auth-container')?.appendChild(errorDiv);
    }
  }
}

/**
 * Show status screen (rejected, suspended) - 'pending' no longer shows a
 * blocking screen here (per Todd 2026-10-24 - a pending agent now goes
 * straight into the app, see proceedWithAgentStatus).
 */
function showStatusScreen(status: 'rejected' | 'suspended', details?: { reason?: string | null }): void {
  authGate.classList.add('hidden');
  agentLayout.classList.add('hidden');

  // Remove any existing status screen
  const existingStatus = document.getElementById('status-screen');
  if (existingStatus) {
    existingStatus.remove();
  }

  const statusScreen = document.createElement('div');
  statusScreen.id = 'status-screen';
  statusScreen.className = 'status-screen';

  let icon = '';
  let title = '';
  let message = '';
  let buttonText = 'Sign Out';

  switch (status) {
    case 'rejected':
      icon = '❌';
      title = 'Application Not Approved';
      message = details?.reason
        ? `Your agent application was not approved. Reason: ${details.reason}`
        : 'Your agent application was not approved. Please contact support for more information.';
      break;
    case 'suspended':
      icon = '⚠️';
      title = 'Account Suspended';
      message = 'Your agent account has been suspended. Please contact support for more information.';
      break;
  }

  statusScreen.innerHTML = `
    <div class="status-container">
      <div class="status-icon">${icon}</div>
      <h1 class="status-title">${title}</h1>
      <p class="status-message">${message}</p>
      ${status === 'rejected' ? '<button id="status-try-again-btn" class="status-btn" style="margin-right: 0.75rem;">Try Again</button>' : ''}
      <button id="status-signout-btn" class="status-btn">${buttonText}</button>
    </div>
  `;

  // Add styles if not already present
  if (!document.getElementById('status-screen-styles')) {
    const styles = document.createElement('style');
    styles.id = 'status-screen-styles';
    styles.textContent = `
      .status-screen {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        z-index: 1000;
      }
      .status-container {
        text-align: center;
        max-width: 480px;
        padding: 2rem;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      .status-icon {
        font-size: 4rem;
        margin-bottom: 1rem;
      }
      .status-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #fff;
        margin-bottom: 1rem;
      }
      .status-message {
        color: rgba(255, 255, 255, 0.7);
        line-height: 1.6;
        margin-bottom: 2rem;
      }
      .status-message strong {
        color: #fff;
      }
      .status-btn {
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #fff;
        padding: 0.75rem 2rem;
        border-radius: 8px;
        cursor: pointer;
        font-size: 1rem;
        transition: all 0.2s ease;
      }
      .status-btn:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.3);
      }
    `;
    document.head.appendChild(styles);
  }

  document.body.appendChild(statusScreen);

  // Add sign out handler
  document.getElementById('status-signout-btn')?.addEventListener('click', async () => {
    await signOut();
    statusScreen.remove();
  });
}

/**
 * Show agent layout, hide auth gate
 */
function showAgentLayout(): void {
  authGate.classList.add('hidden');
  agentLayout.classList.remove('hidden');
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function ensureRegistrationFormStyles(): void {
  if (document.getElementById('registration-form-styles')) return;
  const styles = document.createElement('style');
  styles.id = 'registration-form-styles';
  styles.textContent = `
    .registration-form {
      text-align: left;
      margin: 1.5rem 0;
    }
    .registration-form .form-group {
      margin-bottom: 1rem;
    }
    .registration-form .form-label {
      color: rgba(255, 255, 255, 0.85);
    }
    .registration-form .form-input {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.2);
      color: #fff;
    }
    .registration-form .form-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
    .registration-form .form-hint {
      color: rgba(255, 255, 255, 0.6);
    }
    #registration-error:not(.hidden) {
      margin-bottom: 1rem;
    }
    .status-btn-secondary {
      margin-top: 0.75rem;
      background: transparent;
      border-color: rgba(255, 255, 255, 0.15);
    }
  `;
  document.head.appendChild(styles);
}

/**
 * Shared license/subdomain/website form, used both for a brand-new agent's
 * initial registration and for a rejected agent's resubmission - the two
 * only differ in copy, submit-button label, pre-filled values, and what
 * happens on success (see showRegistrationForm/showResubmitForm below).
 * Pre-filling with the agent's current subdomain (resubmit case) treats
 * that value as already-available without round-tripping checkSubdomain -
 * it's already theirs, so the live-availability check would otherwise
 * incorrectly report it as taken.
 */
function showLicenseSubdomainForm(options: {
  icon: string;
  title: string;
  message: string;
  submitLabel: string;
  initialValues?: { licenseNumber?: string; subdomain?: string; existingWebsite?: string };
  onSubmit: (info: { licenseNumber: string; subdomain: string; existingWebsite?: string }) => Promise<void>;
}): void {
  authGate.classList.add('hidden');
  agentLayout.classList.add('hidden');

  const existingStatus = document.getElementById('status-screen');
  if (existingStatus) {
    existingStatus.remove();
  }

  const initial = options.initialValues || {};
  const originalSubdomain = initial.subdomain || '';

  const screen = document.createElement('div');
  screen.id = 'status-screen';
  screen.className = 'status-screen';
  screen.innerHTML = `
    <div class="status-container">
      <div class="status-icon">${options.icon}</div>
      <h1 class="status-title">${options.title}</h1>
      <p class="status-message">${options.message}</p>
      <form id="registration-form" class="registration-form">
        <div class="form-group">
          <label class="form-label">License Number *</label>
          <input type="text" class="form-input" id="reg-license" required placeholder="RS-12345" value="${escapeAttr(initial.licenseNumber || '')}">
        </div>
        <div class="form-group">
          <label class="form-label">Requested Subdomain *</label>
          <div class="flex items-center gap-2">
            <input type="text" class="form-input" id="reg-subdomain" required placeholder="yourname" style="max-width: 200px;" value="${escapeAttr(originalSubdomain)}">
            <span style="color: rgba(255, 255, 255, 0.6);">.slowmaui.com</span>
          </div>
          <div class="form-hint" id="reg-subdomain-status">${originalSubdomain ? '✓ Currently yours' : ''}</div>
        </div>
        <div class="form-group">
          <label class="form-label">Existing Website (optional)</label>
          <input type="url" class="form-input" id="reg-website" placeholder="https://..." value="${escapeAttr(initial.existingWebsite || '')}">
        </div>
        <p id="registration-error" class="auth-error hidden"></p>
        <button type="submit" class="status-btn" id="registration-submit-btn" style="width: 100%;">${options.submitLabel}</button>
      </form>
      <button id="status-signout-btn" class="status-btn status-btn-secondary">Sign Out</button>
    </div>
  `;

  ensureRegistrationFormStyles();
  document.body.appendChild(screen);

  const form = document.getElementById('registration-form') as HTMLFormElement;
  const errorEl = document.getElementById('registration-error') as HTMLParagraphElement;
  const submitBtn = document.getElementById('registration-submit-btn') as HTMLButtonElement;
  const subdomainInput = document.getElementById('reg-subdomain') as HTMLInputElement;
  const subdomainStatus = document.getElementById('reg-subdomain-status') as HTMLDivElement;

  // Live availability check, same debounced pattern (and the same
  // checkSubdomain endpoint) as Onboarding.ts's old subdomain step.
  let subdomainAvailable = !!originalSubdomain;
  let checkTimeout: number;
  subdomainInput.addEventListener('input', () => {
    const value = subdomainInput.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    subdomainInput.value = value;

    if (value && value === originalSubdomain) {
      subdomainAvailable = true;
      subdomainStatus.textContent = '✓ Currently yours';
      return;
    }
    subdomainAvailable = false;

    if (value.length < 3) {
      subdomainStatus.textContent = 'Minimum 3 characters';
      return;
    }

    subdomainStatus.textContent = 'Checking availability...';
    clearTimeout(checkTimeout);
    checkTimeout = window.setTimeout(async () => {
      try {
        if (!agentClient) return;
        const result = await agentClient.checkSubdomain(value);
        if (subdomainInput.value !== value) return; // stale response
        subdomainAvailable = result.available;
        subdomainStatus.textContent = result.available ? '✓ Available' : '✗ Already taken';
      } catch {
        subdomainStatus.textContent = 'Error checking availability';
      }
    }, 500);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const licenseNumber = (document.getElementById('reg-license') as HTMLInputElement).value.trim();
    const existingWebsite = (document.getElementById('reg-website') as HTMLInputElement).value.trim();
    const subdomain = subdomainInput.value.trim();

    errorEl.classList.add('hidden');

    if (!subdomain || !subdomainAvailable) {
      errorEl.textContent = 'Please choose an available subdomain.';
      errorEl.classList.remove('hidden');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    try {
      await options.onSubmit({
        licenseNumber,
        subdomain,
        ...(existingWebsite ? { existingWebsite } : {}),
      });
      screen.remove();
    } catch (error) {
      console.error('Submit failed:', error);
      errorEl.textContent = error instanceof ApiError ? error.message : 'Something went wrong. Please try again.';
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      submitBtn.textContent = options.submitLabel;
    }
  });

  document.getElementById('status-signout-btn')?.addEventListener('click', async () => {
    await signOut();
    screen.remove();
  });
}

/**
 * One-time form for a brand-new agent - collects a required license number
 * and subdomain, plus an optional existing website, before the agent row
 * is even created, so admin approval (apps/admin's Agents page) always has
 * something to vet an application on. Shown when register() throws
 * ApiError('LICENSE_REQUIRED') (see @slowmaui/api-client's
 * AgentClient.register) - every returning agent already has a record and
 * never hits this path.
 */
function showRegistrationForm(user: User): void {
  showLicenseSubdomainForm({
    icon: '📋',
    title: 'Finish Registering',
    message: 'We need a couple more details before we can review your application.',
    submitLabel: 'Submit',
    onSubmit: async (info) => {
      if (!agentClient) throw new Error('Not signed in');
      const status = await agentClient.register(info);
      await proceedWithAgentStatus(user, status);
    },
  });
}

/**
 * Lets a rejected agent correct whatever the rejection reason flagged
 * (license number, subdomain, website) and resubmit for another review,
 * rather than being stuck forever on the rejected screen with no path
 * forward except signing out. Pre-filled with their current values via
 * AgentClient.resubmit - see the "Try Again" button wired up in
 * proceedWithAgentStatus's 'rejected' case.
 */
function showResubmitForm(user: User, current: AgentAuthStatus): void {
  showLicenseSubdomainForm({
    icon: '🔁',
    title: 'Resubmit Application',
    message: 'Update anything that needs correcting, then resubmit for another review.',
    submitLabel: 'Resubmit for Review',
    initialValues: {
      licenseNumber: current.license_number || '',
      subdomain: current.subdomain || '',
      existingWebsite: current.existing_website || '',
    },
    onSubmit: async (info) => {
      if (!agentClient) throw new Error('Not signed in');
      const status = await agentClient.resubmit(info);
      await proceedWithAgentStatus(user, status);
    },
  });
}

/**
 * Handles an agent's current status once register() has resolved
 * successfully - shared by the normal sign-in path and by
 * showRegistrationForm's submit handler, which reaches the same point via a
 * different route (the form's own call to register(), rather than the
 * sign-in effect's first no-argument call).
 */
async function proceedWithAgentStatus(user: User, status: AgentAuthStatus): Promise<void> {
  agentStatus = status;
  console.log('Agent status:', agentStatus.status);
  setCanEditDefaults(agentStatus.can_edit_defaults === 1);
  updateAdminModeUI();

  // Handle different statuses. 'pending' falls through just like 'approved'
  // now (per Todd 2026-10-24) - an agent can use the whole app immediately;
  // only publishing (and a couple of other outbound actions, gated
  // server-side) require full approval. 'rejected' and 'suspended' still
  // block entirely.
  switch (agentStatus.status) {
    case 'rejected':
      showStatusScreen('rejected', { reason: agentStatus.rejection_reason });
      document.getElementById('status-try-again-btn')?.addEventListener('click', () => {
        showResubmitForm(user, status);
      });
      return;

    case 'suspended':
      showStatusScreen('suspended');
      return;

    case 'pending':
    case 'approved':
      // Continue to agent layout
      break;
  }

  // Agent is approved - show the agent layout
  const agentProfile = toAgentProfile(agentStatus);

  // Update UI
  updateUserInfo(user);
  if (agentProfile?.subdomain) {
    updateSubdomainDisplay(agentProfile);
  } else {
    agentSubdomain.innerHTML = '<span class="subdomain-pending">Subdomain pending</span>';
  }
  showAgentLayout();

  // App-shell-level Cart drawer - instantiated once (guarded, since this
  // whole callback can re-run on auth state changes), not per-page. See
  // state/cartDrawer.ts for how pages reach it.
  if (!cartDrawer && agentClient) {
    const cartDrawerHost = document.getElementById('cart-drawer-host') as HTMLElement;
    cartDrawer = new CartDrawer({
      container: cartDrawerHost,
      client: agentClient,
      onOpen: updateMainContentShrink,
      onClose: updateMainContentShrink,
      onResize: updateMainContentShrink,
      resolveListingLabels: async (ids) => {
        try {
          const results = await dataApi.getListingsByIds(ids);
          return new Map(results.map(l => [l.id, { label: formatListingAddress(l), status: l.status }]));
        } catch (error) {
          console.error('[main] Failed to resolve listing labels:', error);
          return new Map();
        }
      },
      resolveNeighborhoodLabel: (id) => shellNeighborhoods.find(n => n.id === id)?.name,
      resolveTmkLabels: async (tmks) => {
        try {
          const results = await dataApi.getTmksByIds(tmks);
          return new Map(results.map(t => [t.tmkNormalized, { label: t.address + (t.unit ? ` #${t.unit}` : '') }]));
        } catch (error) {
          console.error('[main] Failed to resolve TMK labels:', error);
          return new Map();
        }
      },
    });
    setCartDrawer(cartDrawer);

    void dataApi.getNeighborhoods({ filters: { visible: true }, limit: 500 })
      .then((response) => { shellNeighborhoods = response.data; })
      .catch((error) => console.error('[main] Failed to load neighborhoods for cart drawer:', error));
  }

  // App-shell-level List drawer (migration 071, per Todd 2026-10-30) -
  // same once-only instantiation reasoning as Cart's above. Replaces
  // Cart's presence on the Contacts page specifically; see
  // state/listDrawer.ts for how pages reach it.
  if (!listDrawer && agentClient) {
    const listDrawerHost = document.getElementById('list-drawer-host') as HTMLElement;
    listDrawer = new ListDrawer({
      container: listDrawerHost,
      client: agentClient,
      onOpen: updateMainContentShrink,
      onClose: updateMainContentShrink,
      onResize: updateMainContentShrink,
    });
    setListDrawer(listDrawer);
  }

  // "Acting for <contact>" header badge (per Todd 2026-11-03) - registered
  // once; state/selectedContact.ts calls this on every select/clear so the
  // badge stays in sync regardless of which page did the selecting (today,
  // only Contacts.ts does). See index.html's .header-right for the markup.
  if (!selectedContactListenerWired) {
    selectedContactListenerWired = true;
    onSelectedContactChange(() => {
      const contact = getSelectedContact();
      selectedContactBadge.classList.toggle('hidden', !contact);
      selectedContactName.textContent = contact ? `Acting for: ${contact.name || contact.email}` : '';
    });
    selectedContactClear.addEventListener('click', () => clearSelectedContact());
  }

  // Initialize router and navigate
  router.init(pageContent, agentClient!, agentProfile);
  router.navigate(window.location.hash || '#/dashboard');
}

/**
 * Update user info display
 */
function updateUserInfo(user: User): void {
  userAvatar.src = user.photoURL || '';
  userAvatar.alt = user.displayName || 'User';
  userName.textContent = user.displayName || user.email || 'Agent';
}

/**
 * Update agent subdomain display
 */
function updateSubdomainDisplay(profile: AgentProfile): void {
  // Handle subdomain that may already include domain suffix
  const subdomain = profile.subdomain?.replace(/\.slowmaui\.com$/i, '') || 'agent';
  agentSubdomain.innerHTML = `
    <a href="https://${subdomain}.slowmaui.com" target="_blank" class="subdomain-link">
      ${subdomain}.slowmaui.com
      <span class="external-icon">↗</span>
    </a>
  `;
}

/**
 * Reflect the current can-use / is-in admin mode state into the user menu
 * (switch + row visibility), the header badge, and any nav items that are
 * admin-only (today: SEO Pages). Called on login and whenever the switch
 * changes.
 */
function updateAdminModeUI(): void {
  const canUse = canUseAdminMode();
  const active = isAdminMode();

  adminModeRow.classList.toggle('hidden', !canUse);
  adminModeToggle.checked = active;
  adminModeBadge.classList.toggle('hidden', !active);
  seoNavItem.classList.toggle('hidden', !active);
  suggestionsNavItem.classList.toggle('hidden', !active);

  // Don't strand the user on an admin-only page they just lost access to.
  if (!active && (window.location.hash === '#/seo' || window.location.hash === '#/suggestions')) {
    router.navigate('#/dashboard');
  }

  // Content's route loader picks ContentGenerator vs AgentContent based on
  // admin mode at navigation time - if they're sitting on #/content when
  // the switch flips, re-navigate so the module actually swaps instead of
  // leaving the wrong one on screen until they browse away and back.
  if (window.location.hash === '#/content') {
    router.navigate('#/content');
  }
}

adminModeToggle.addEventListener('change', () => {
  setAdminMode(adminModeToggle.checked);
});

window.addEventListener('admin-mode-change', updateAdminModeUI);

/**
 * Handle authentication state changes
 */
onAuthStateChange(async (user) => {
  currentUser = user;

  if (user) {
    // Initialize agent client
    agentClient = new AgentClient({
      baseUrl: import.meta.env.VITE_AGENT_API_URL || 'https://slowmaui-agent-api.slowmaui-mls-api.workers.dev',
      getToken: getIdToken,
    });

    try {
      // Register or update agent record (this creates the agent if new, or
      // updates last_login). A brand-new agent has no license number yet -
      // the server rejects with LICENSE_REQUIRED instead of creating a bare
      // row, so we catch that specifically and collect it first.
      const status = await agentClient.register();
      await proceedWithAgentStatus(user, status);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'LICENSE_REQUIRED') {
        showRegistrationForm(user);
        return;
      }
      console.error('Error during agent registration:', error);
      showAuthGate('Unable to connect to agent service. Please try again.');
    }
  } else {
    agentClient = null;
    agentStatus = null;
    setCanEditDefaults(false);
    updateAdminModeUI();
    showAuthGate();
  }
});

// Sign in with Google
signInGoogleBtn.addEventListener('click', async () => {
  try {
    signInGoogleBtn.disabled = true;
    signInGoogleBtn.textContent = 'Signing in...';
    await signInWithGoogle();
  } catch (error) {
    console.error('Sign in error:', error);
    showAuthGate('Sign in failed. Please try again.');
  } finally {
    signInGoogleBtn.disabled = false;
    signInGoogleBtn.innerHTML = '<span class="btn-icon">G</span> Sign in with Google';
  }
});

// Sign in with Apple
signInAppleBtn.addEventListener('click', async () => {
  try {
    signInAppleBtn.disabled = true;
    signInAppleBtn.textContent = 'Signing in...';
    await signInWithApple();
  } catch (error) {
    console.error('Sign in error:', error);
    showAuthGate('Sign in failed. Please try again.');
  } finally {
    signInAppleBtn.disabled = false;
    signInAppleBtn.innerHTML = '<span class="btn-icon"></span> Sign in with Apple';
  }
});

// Sign out button click
signOutBtn.addEventListener('click', async () => {
  try {
    userDropdown.classList.add('hidden');
    await signOut();
  } catch (error) {
    console.error('Sign out error:', error);
  }
});

// User menu toggle
userMenuBtn.addEventListener('click', () => {
  userDropdown.classList.toggle('hidden');
});

// Hamburger toggle (mobile only - CSS hides the button entirely above
// the 768px breakpoint, see .hamburger-btn) - reuses the same #top-tabs
// nav rather than a separate mobile menu structure; CSS alone reflows it
// from a horizontal scroller into a vertical dropdown when .mobile-open
// is present.
hamburgerBtn.addEventListener('click', () => {
  const isOpen = topTabsNav.classList.toggle('mobile-open');
  hamburgerBtn.setAttribute('aria-expanded', String(isOpen));
});

// Close the user-menu dropdown / mobile nav when clicking outside.
document.addEventListener('click', (e) => {
  if (!userMenuBtn.contains(e.target as Node) && !userDropdown.contains(e.target as Node)) {
    userDropdown.classList.add('hidden');
  }
  if (!hamburgerBtn.contains(e.target as Node) && !topTabsNav.contains(e.target as Node)) {
    topTabsNav.classList.remove('mobile-open');
    hamburgerBtn.setAttribute('aria-expanded', 'false');
  }
});

// Navigation click handler
navItems.forEach((item) => {
  item.addEventListener('click', (e) => {
    e.preventDefault();
    const href = (item as HTMLAnchorElement).getAttribute('href');
    if (href) {
      router.navigate(href);
    }
    // Close the mobile nav after a selection - a no-op on desktop since
    // .mobile-open is never added there.
    topTabsNav.classList.remove('mobile-open');
    hamburgerBtn.setAttribute('aria-expanded', 'false');
  });
});

// Handle browser back/forward
window.addEventListener('hashchange', () => {
  if (currentUser) {
    router.navigate(window.location.hash);
  }
});

// Update active nav item (called via event from router)
window.addEventListener('route-change', ((e: CustomEvent<{ page: string }>) => {
  navItems.forEach((item) => {
    const itemPage = item.getAttribute('data-page');
    if (itemPage === e.detail.page) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });

  // Cart drawer is available on Map, Messaging, and Recent Changes (per
  // Todd 2026-10-30 - swapped Contacts out for Market Activity, since
  // List drawer takes Cart's old spot on Contacts below). List drawer is
  // Contacts-only.
  currentPage = e.detail.page;
  cartDrawer?.setAvailable(['map', 'messaging', 'market-activity'].includes(currentPage));
  listDrawer?.setAvailable(currentPage === 'contacts');
  // Re-evaluate on every navigation, not just open()/close() - a drawer
  // stays open across page changes (it's app-shell-level), so arriving
  // at or leaving one of the shrink-pages with it already open still
  // needs #main-content's margin added or removed.
  updateMainContentShrink();

  // Search is only relevant on Properties/Map (per Todd 2026-10-24) -
  // shown/hidden here instead of living permanently in the header.
  document.getElementById('search-subheader')?.classList.toggle('hidden', currentPage !== 'map');
}) as EventListener);

/**
 * Header search - Neighborhoods/Listings/TMK suggestions only, same
 * /search/suggest endpoint apps/public's search box uses. No Ask/natural-
 * language row (that's tied to apps/public's Ask-mode state machine, which
 * the Agent Map deliberately doesn't have). A result click sets a pending
 * map intent and navigates to #/map - works the same whether the agent is
 * already on the map or on Dashboard/Contacts/Profile when they search.
 */
function setupSearch(): void {
  const input = document.getElementById('search-input') as HTMLInputElement | null;
  const results = document.getElementById('search-results');
  const clearBtn = document.getElementById('search-clear');
  if (!input || !results) return;

  let debounceTimer: number;

  const updateClearButton = () => {
    clearBtn?.classList.toggle('hidden', input.value.length === 0);
  };

  const formatPrice = (price: number) => {
    if (price >= 1000000) return `$${(price / 1000000).toFixed(2)}M`;
    return `$${(price / 1000).toFixed(0)}K`;
  };

  const renderResult = (s: {
    type: string; value: string; id?: string; lat?: number; lng?: number;
    listingId?: string; listPrice?: number; status?: string;
  }) => {
    let subtitle = '';
    if (s.type === 'listing' && s.listPrice) {
      subtitle = `${formatPrice(s.listPrice)} • ${s.status || 'Active'}`;
      if (s.listingId) subtitle += ` • MLS# ${s.listingId}`;
    }
    const typeColors: Record<string, string> = { neighborhood: '#16a34a', listing: '#2563eb', tmk: '#6b7280' };
    const typeLabels: Record<string, string> = { neighborhood: 'Neighborhood', listing: 'Listing', tmk: 'Property' };

    return `
      <div class="search-result" data-type="${s.type}" data-id="${s.id || ''}" data-lat="${s.lat ?? ''}" data-lng="${s.lng ?? ''}" data-value="${s.value}">
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${s.value}</div>
          ${subtitle ? `<div style="font-size: 0.75rem; color: var(--color-text-light); margin-top: 2px;">${subtitle}</div>` : ''}
        </div>
        <span style="background: ${typeColors[s.type] || '#6b7280'}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; font-weight: 600; margin-left: 8px; white-space: nowrap;">
          ${typeLabels[s.type] || s.type}
        </span>
      </div>
    `;
  };

  const bindResultClicks = () => {
    results.querySelectorAll('.search-result').forEach((el) => {
      el.addEventListener('click', () => {
        const dataset = (el as HTMLElement).dataset;
        const type = dataset.type;
        const id = dataset.id;
        const lat = parseFloat(dataset.lat || '');
        const lng = parseFloat(dataset.lng || '');
        const value = dataset.value || '';

        input.value = value;
        updateClearButton();
        results.classList.remove('visible');

        if (type === 'neighborhood' && id) {
          setPendingMapIntent({ type: 'neighborhood', id, name: value });
          router.navigate('#/map');
        } else if ((type === 'listing' || type === 'tmk') && !isNaN(lat) && !isNaN(lng)) {
          setPendingMapIntent({ type: 'listing', lat, lng });
          router.navigate('#/map');
        }
      });
    });
  };

  input.addEventListener('input', () => {
    updateClearButton();
    clearTimeout(debounceTimer);
    const query = input.value.trim();

    if (query.length < 2) {
      results.innerHTML = '';
      results.classList.remove('visible');
      return;
    }

    results.innerHTML = '<div class="search-loading-row">Searching…</div>';
    results.classList.add('visible');

    debounceTimer = window.setTimeout(async () => {
      try {
        const suggestions = await dataApi.searchSuggest({ q: query, limit: 8 });
        if (input.value.trim() !== query) return;
        results.innerHTML = suggestions.length
          ? suggestions.map(renderResult).join('')
          : '<div class="search-loading-row">No matches</div>';
        bindResultClicks();
      } catch {
        if (input.value.trim() !== query) return;
        results.innerHTML = '<div class="search-loading-row">Search failed</div>';
      }
    }, 200);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && results.classList.contains('visible')) {
      results.classList.remove('visible');
    }
  });

  clearBtn?.addEventListener('click', () => {
    input.value = '';
    updateClearButton();
    results.innerHTML = '';
    results.classList.remove('visible');
    input.focus();
  });

  document.addEventListener('click', (e) => {
    if (!input.contains(e.target as Node) && !results.contains(e.target as Node)) {
      results.classList.remove('visible');
    }
  });
}

setupSearch();

// Export for external use
export { agentClient, agentStatus };
